import React from 'react';
import {
  HIERARCHY_LABELS, formatNumber, formatRelative, formatMinutes,
} from './analyticsUtils';

const COLUMNS = [
  { key: 'name', label: 'Mentor', sortable: true, align: 'left' },
  { key: null, label: 'Role', align: 'left' },
  { key: 'events', label: 'Events', sortable: true, align: 'right' },
  { key: 'sessions', label: 'Sessions', sortable: true, align: 'right' },
  { key: 'activeDays', label: 'Active days', sortable: true, align: 'right' },
  { key: 'foreground', label: 'Time in app', sortable: true, align: 'right' },
  { key: null, label: 'Avg session', align: 'right' },
  { key: 'lastSeen', label: 'Last seen', sortable: true, align: 'right' },
  { key: null, label: 'Version', align: 'left' },
];

const MentorUsageTable = ({
  data, loading, sort, order, onSortChange, onSelectMentor, onPageChange,
}) => {
  if (!data) return null;
  const { mentors, pagination } = data;

  const handleSort = (key) => {
    if (!key) return;
    if (sort === key) {
      onSortChange(key, order === 'desc' ? 'asc' : 'desc');
    } else {
      onSortChange(key, key === 'name' ? 'asc' : 'desc');
    }
  };

  return (
    <section className="analytics-card analytics-table-card">
      <div className="analytics-table-header">
        <h3 className="analytics-card-title">
          Mentors
          <span className="analytics-table-count">{formatNumber(pagination.total)}</span>
        </h3>
      </div>

      <div className="analytics-table-scroll">
        <table className="analytics-table">
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.label}
                  className={`text-${col.align}${col.sortable ? ' sortable' : ''}${sort === col.key ? ' sorted' : ''}`}
                  onClick={() => handleSort(col.key)}
                  role={col.sortable ? 'button' : undefined}
                  aria-sort={
                    sort === col.key
                      ? (order === 'asc' ? 'ascending' : 'descending')
                      : undefined
                  }
                >
                  {col.label}
                  {col.sortable && sort === col.key && (
                    <i
                      className={`bi ${order === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill'} analytics-sort-icon`}
                      aria-hidden="true"
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mentors.length === 0 && !loading && (
              <tr>
                <td colSpan={COLUMNS.length} className="analytics-table-empty">
                  No mentors match these filters.
                </td>
              </tr>
            )}
            {mentors.map((m) => (
              <tr
                key={m.id}
                className={`analytics-table-row${m.neverActive ? ' inactive' : ''}`}
                onClick={() => onSelectMentor(m.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectMentor(m.id);
                  }
                }}
              >
                <td className="text-left">
                  <div className="analytics-mentor-name">{m.name}</div>
                  <div className="analytics-mentor-email">{m.email || m.username}</div>
                </td>
                <td className="text-left">
                  <span className="analytics-role-badge">
                    {HIERARCHY_LABELS[m.hierarchy] || m.hierarchy}
                  </span>
                </td>
                <td className="text-right">{formatNumber(m.events)}</td>
                <td className="text-right">{formatNumber(m.sessions)}</td>
                <td className="text-right">{formatNumber(m.activeDays)}</td>
                <td className="text-right">{m.foregroundHours}h</td>
                <td className="text-right">{formatMinutes(m.avgSessionMinutes)}</td>
                <td className="text-right">
                  <span className={m.neverActive ? 'analytics-never' : ''}>
                    {formatRelative(m.lastSeenAt)}
                  </span>
                </td>
                <td className="text-left">
                  {m.appVersion
                    ? <span className="analytics-version">{m.appVersion}</span>
                    : <span className="analytics-muted">--</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination.totalPages > 1 && (
        <div className="analytics-pagination">
          <button
            type="button"
            className="analytics-page-btn"
            onClick={() => onPageChange(pagination.page - 1)}
            disabled={pagination.page <= 1 || loading}
          >
            <i className="bi bi-chevron-left" aria-hidden="true" /> Prev
          </button>
          <span className="analytics-page-info">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            type="button"
            className="analytics-page-btn"
            onClick={() => onPageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages || loading}
          >
            Next <i className="bi bi-chevron-right" aria-hidden="true" />
          </button>
        </div>
      )}
    </section>
  );
};

export default MentorUsageTable;
